import {
    AUTH0_AUTOSHIP_ENROLL_SESSION_KEY,
    AUTH0_LOGIN_COMPLETED_SESSION_KEY,
    AUTH0_LOGIN_METHOD_SESSION_KEY,
} from '@libs/core';

import { BREAKPOINTS } from '../constants';
import { mutateSessionStorage, withSessionStorage } from './sessionStorageAccess';

export const AUTH0_LOGIN_METHODS = ['login page', 'login modal', 'checkout'] as const;

export type Auth0LoginMethod = (typeof AUTH0_LOGIN_METHODS)[number];

/** Carries the checkout interstitial source across `/sign-in` without writing session storage first. */
export const AUTH0_LOGIN_METHOD_QUERY_PARAM = 'loginMethod';

let hasConsumedCompletedLogin = false;
let hasConsumedAuth0AutoshipEnroll = false;

const AUTOSHIP_ENROLL_PENDING = 'pending';
const AUTOSHIP_ENROLL_CONFIRMED = 'callback-confirmed';
const AUTOSHIP_ENROLL_COMPLETE = 'completed';

function isAuth0LoginMethod(value: string | null | undefined): value is Auth0LoginMethod {
    return AUTH0_LOGIN_METHODS.includes(value as Auth0LoginMethod);
}

/**
 * Desktop and tablet map to the legacy full-page sign-in surface; mobile web
 * maps to the legacy sign-in drawer. Auth0 always uses Universal Login, so
 * viewport is the durable stand-in for header and `/sign-in` `login_method`.
 * Checkout is never inferred here — the interstitial passes `'checkout'` on
 * the `/sign-in` URL so authorize can persist it. A `/checkout` return path
 * cannot stand in for that source.
 */
export function resolveAuth0LoginMethod(): Auth0LoginMethod {
    try {
        if (globalThis.window.matchMedia(`(max-width: ${BREAKPOINTS.md - 1}px)`).matches) {
            return 'login modal';
        }
    } catch {
        return 'login page';
    }

    return 'login page';
}

export function readPersistedAuth0LoginMethod(): Auth0LoginMethod | undefined {
    return withSessionStorage({
        fallback: undefined,
        read: storage => {
            const stored = storage.getItem(AUTH0_LOGIN_METHOD_SESSION_KEY);
            return isAuth0LoginMethod(stored) ? stored : undefined;
        },
    });
}

/**
 * Captures `login_method` before the full-page Auth0 redirect so it is still
 * available after Universal Login returns to PetSmart. Call only from
 * login/signup authorize entry points — not from already-authenticated Auth0
 * flows, and not from the checkout interstitial CTA. Pass `'checkout'` when
 * `/sign-in` carries the interstitial query param. Viewport is used when omitted.
 */
export function persistAuth0LoginMethod(loginMethod: Auth0LoginMethod = resolveAuth0LoginMethod()): void {
    mutateSessionStorage(storage => {
        storage.setItem(AUTH0_LOGIN_METHOD_SESSION_KEY, loginMethod);
    });
}

/**
 * Drops a leftover `login_method` that never reached Auth0 authorize. `/sign-in`
 * must call this when an existing session skips Universal Login so a later
 * change-email or `/sign-in` visit cannot arm or attribute `login` as checkout.
 */
export function clearPersistedAuth0LoginMethod(): void {
    mutateSessionStorage(storage => {
        storage.removeItem(AUTH0_LOGIN_METHOD_SESSION_KEY);
    });
}

export function readCheckoutAuth0LoginMethodParam(
    searchParams?: { get(name: string): string | null } | null
): Extract<Auth0LoginMethod, 'checkout'> | undefined {
    return searchParams?.get(AUTH0_LOGIN_METHOD_QUERY_PARAM) === 'checkout' ? 'checkout' : undefined;
}

/**
 * Arms analytics only for an Auth0 redirect started by in-store Autoship.
 * Every other login/signup redirect clears stale state from a cancelled attempt.
 */
export function persistAuth0AutoshipEnrollIntent(isInStoreAutoship: boolean): void {
    hasConsumedAuth0AutoshipEnroll = false;

    mutateSessionStorage(storage => {
        if (isInStoreAutoship) {
            storage.setItem(AUTH0_AUTOSHIP_ENROLL_SESSION_KEY, AUTOSHIP_ENROLL_PENDING);
            return;
        }

        storage.removeItem(AUTH0_AUTOSHIP_ENROLL_SESSION_KEY);
    });
}

/**
 * Binds a pending intent to the Auth0 transaction that returned it in appState.
 * Unrelated callbacks clear stale intent instead of completing it.
 */
export function confirmAuth0AutoshipEnrollCallback(isInStoreAutoship: boolean): void {
    mutateSessionStorage(storage => {
        const stored = storage.getItem(AUTH0_AUTOSHIP_ENROLL_SESSION_KEY);

        if (isInStoreAutoship && stored === AUTOSHIP_ENROLL_PENDING) {
            storage.setItem(AUTH0_AUTOSHIP_ENROLL_SESSION_KEY, AUTOSHIP_ENROLL_CONFIRMED);
            return;
        }

        storage.removeItem(AUTH0_AUTOSHIP_ENROLL_SESSION_KEY);
    });
}

/**
 * One-time completed-login signal. Arms analytics only when
 * `persistAuth0LoginMethod` ran before this callback. Change-email and other
 * already-authenticated Auth0 returns share `/auth/callback` and must not fire
 * `login`. Storage failures must not throw — callback redirect still has to run.
 */
export function markAuth0LoginCompleted(): void {
    hasConsumedCompletedLogin = false;

    const loginMethod = readPersistedAuth0LoginMethod();
    if (!loginMethod) {
        return;
    }

    mutateSessionStorage(storage => {
        storage.setItem(AUTH0_LOGIN_COMPLETED_SESSION_KEY, loginMethod);
    });
}

/**
 * Promotes a callback-confirmed in-store Autoship redirect only after token sync
 * succeeds. A cancelled, failed, or unrelated callback cannot fire the event.
 */
export function markAuth0AutoshipEnrollComplete(): void {
    hasConsumedAuth0AutoshipEnroll = false;

    mutateSessionStorage(storage => {
        if (storage.getItem(AUTH0_AUTOSHIP_ENROLL_SESSION_KEY) === AUTOSHIP_ENROLL_CONFIRMED) {
            storage.setItem(AUTH0_AUTOSHIP_ENROLL_SESSION_KEY, AUTOSHIP_ENROLL_COMPLETE);
        }
    });
}

/**
 * Completes in-store Autoship analytics when Universal Login is intentionally
 * skipped because the Auth0 SDK already has an authenticated session.
 */
export function markAuth0AutoshipEnrollCompleteAuthed(): void {
    hasConsumedAuth0AutoshipEnroll = false;

    mutateSessionStorage(storage => {
        storage.setItem(AUTH0_AUTOSHIP_ENROLL_SESSION_KEY, AUTOSHIP_ENROLL_COMPLETE);
    });
}

/**
 * True when a completed Auth0 login is waiting to be reported. Does not consume
 * the signal, so a Strict Mode remount can still fire after the first effect
 * is cancelled mid-claims-fetch.
 */
export function hasPendingAuth0LoginCompleted(): boolean {
    if (hasConsumedCompletedLogin) {
        return false;
    }

    return withSessionStorage({
        fallback: false,
        read: storage => isAuth0LoginMethod(storage.getItem(AUTH0_LOGIN_COMPLETED_SESSION_KEY)),
    });
}

export function hasPendingAuth0AutoshipEnroll(): boolean {
    if (hasConsumedAuth0AutoshipEnroll) {
        return false;
    }

    return withSessionStorage({
        fallback: false,
        read: storage => storage.getItem(AUTH0_AUTOSHIP_ENROLL_SESSION_KEY) === AUTOSHIP_ENROLL_COMPLETE,
    });
}

/**
 * Returns the preserved `login_method` for a newly completed Auth0 login and
 * removes the signal so callback re-renders, destination reloads, and later
 * visits with an existing Auth0 session cannot fire again.
 */
export function consumeAuth0LoginCompleted(): Auth0LoginMethod | null {
    if (hasConsumedCompletedLogin) {
        return null;
    }

    const stored = withSessionStorage({
        fallback: null,
        read: storage => storage.getItem(AUTH0_LOGIN_COMPLETED_SESSION_KEY),
    });

    mutateSessionStorage(storage => {
        storage.removeItem(AUTH0_LOGIN_COMPLETED_SESSION_KEY);
        storage.removeItem(AUTH0_LOGIN_METHOD_SESSION_KEY);
    });

    if (!isAuth0LoginMethod(stored)) {
        return null;
    }

    hasConsumedCompletedLogin = true;
    return stored;
}

/**
 * Consumes the completed in-store Autoship signal independently from login
 * analytics so both events can fire after the same successful Auth0 return.
 */
export function consumeAuth0AutoshipEnroll(): boolean {
    if (hasConsumedAuth0AutoshipEnroll) {
        return false;
    }

    const isCompleted = withSessionStorage({
        fallback: false,
        read: storage => storage.getItem(AUTH0_AUTOSHIP_ENROLL_SESSION_KEY) === AUTOSHIP_ENROLL_COMPLETE,
    });

    mutateSessionStorage(storage => {
        storage.removeItem(AUTH0_AUTOSHIP_ENROLL_SESSION_KEY);
    });

    if (!isCompleted) {
        return false;
    }

    hasConsumedAuth0AutoshipEnroll = true;
    return true;
}
